  import { useState, useEffect, useRef } from "react"
  import { Flex, Space } from "antd"
  import { Title } from "ui/Typography"
  import { useTranslation } from "react-i18next"
  import BookingDetailsHeader from "./Header"
  import BookingDetailsBody from "./Body"
  import CrewTab from "./Body/CrewTab"
  import EquipmentTab from "./Body/EquipmentTab"
  import DSTab from "ui/DSTab"
  import PropTypes from "prop-types"
  import { useSelector, useDispatch } from "react-redux"
  import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
  import { usePermissions } from "hooks/usePermissions"
  import { MODULES, ACTIONS } from "utils/permissions"
  import styles from "./index.module.css"
  import { GuestWaiverDetails } from "./Body/GuestWaiverDetails"
  import { isFeatureEnabled } from "config/features"
  import { getClientBookingDetails } from "store/bookings"

  const BookingDetails = ({ isShowTitle = true, uuid, disableLoaderWatcher = false, onCancelSuccess, onAssignmentChange }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { data } = useSelector((store) => store?.bookings?.clientBookingDetails) || {}
    const [activeTab, setActiveTab] = useState("details")
    const prevTabRef = useRef("details")
    const { hasPermission } = usePermissions()
    useLoaderWatchers({ watchers: disableLoaderWatcher ? [] : ["bookings.clientBookingDetails"] })

    useEffect(() => {
      if (activeTab === "details" && prevTabRef.current !== "details" && uuid) {
        dispatch(getClientBookingDetails(uuid))
      }
      prevTabRef.current = activeTab
    }, [activeTab, uuid, dispatch])

    const tabItems = [
      { key: "details", label: t("booking.tabs.details") || "Details" },
      { key: "crew", label: t("booking.tabs.crew") || "Crew" },
      hasPermission(MODULES.EQUIPMENT, ACTIONS.VIEW) && { key: "equipment", label: t("booking.tabs.equipment") || "Equipment" }
    ]?.filter(Boolean)

    useEffect(() => {
      const availableTabKeys = tabItems?.map((item) => item.key)
      if (!availableTabKeys.includes(activeTab)) {
        setActiveTab("details")
      }
    }, [tabItems, activeTab])

    const renderTabContent = () => {
      switch (activeTab) {
        case "details":
          return <BookingDetailsBody booking={data} />
        case "crew":
          return <CrewTab booking={data} onAssignmentChange={onAssignmentChange} />
        case "equipment":
          return <EquipmentTab booking={data} onAssignmentChange={onAssignmentChange} />
        default:
          return <BookingDetailsBody booking={data} />
      }
    }

    return (
      <Space direction="vertical" size={32} className="w-full">
        {isShowTitle && <Title level={3}>{t("client.booking_details")}</Title>}
        <BookingDetailsHeader booking={data} className={`${isShowTitle ? "" : styles.bookingdetails}`} onCancelSuccess={onCancelSuccess} />
        <DSTab items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
        <Flex vertical gap={10} className={styles.tabContent}>
          {renderTabContent()}
          {isFeatureEnabled('CUSTOM_TEMPLATE') && uuid && data?.has_waivers !== false && <GuestWaiverDetails uuid={uuid} />}
        </Flex>
      </Space>
    )
  } 

  BookingDetails.propTypes = {
    isShowTitle: PropTypes.bool,
    uuid: PropTypes.string,
    disableLoaderWatcher: PropTypes.bool,
    onCancelSuccess: PropTypes.func,
    onAssignmentChange: PropTypes.func
  }

  export default BookingDetails
